<script setup>
import { computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';

import Banner from 'dashboard/components/ui/Banner.vue';

const props = defineProps({
  message: {
    type: String,
    default: '',
  },
  isOnPrivateNote: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();
const { t } = useI18n();

const currentChat = useMapGetter('getSelectedChat');
const currentUser = useMapGetter('getCurrentUser');

const activeAgentBot = computed(() =>
  store.getters['agentBots/getActiveAgentBot'](currentChat.value?.inbox_id)
);

const assignedAgent = computed({
  get() {
    return currentChat.value?.meta?.assignee;
  },
  set(agent) {
    const agentId = agent ? agent.id : null;
    store.dispatch('setCurrentChatAssignee', {
      conversationId: currentChat.value?.id,
      assignee: agent,
      assigneeType: agent ? 'User' : null,
    });
    store.dispatch('assignAgent', {
      conversationId: currentChat.value?.id,
      agentId,
    });
  },
});

const hasMessage = computed(() => props.message !== '');
const isUserTyping = computed(() => hasMessage.value && !props.isOnPrivateNote);
const isUnassigned = computed(() => !assignedAgent.value);
const isAssignedToOtherAgent = computed(
  () => assignedAgent.value?.id !== currentUser.value?.id
);

const showSelfAssignBanner = computed(() => {
  return (
    isUserTyping.value && (isUnassigned.value || isAssignedToOtherAgent.value)
  );
});

const isAgentBotOwned = computed(
  () => currentChat.value?.meta?.assignee_type === 'AgentBot'
);
const canSwitchToAgentBot = computed(
  () => !isAgentBotOwned.value && Boolean(activeAgentBot.value?.id)
);

const showBotHandoffBanner = computed(() => {
  return isAgentBotOwned.value;
});

const selfAssignConversation = async () => {
  const { avatar_url, ...rest } = currentUser.value || {};
  assignedAgent.value = { ...rest, thumbnail: avatar_url };
};

const needsAssignmentToCurrentUser = computed(() => {
  return isUnassigned.value || isAssignedToOtherAgent.value;
});

const onClickSelfAssign = async () => {
  try {
    await selfAssignConversation();
    useAlert(t('CONVERSATION.CHANGE_AGENT'));
  } catch (error) {
    useAlert(t('CONVERSATION.CHANGE_AGENT_FAILED'));
  }
};

const onClickBotHandoff = async () => {
  try {
    if (isAgentBotOwned.value || needsAssignmentToCurrentUser.value) {
      await selfAssignConversation();
    }

    useAlert(t('CONVERSATION.BOT_HANDOFF_SUCCESS'));
  } catch (error) {
    useAlert(t('CONVERSATION.BOT_HANDOFF_ERROR'));
  }
};

const switchToAgentBot = async () => {
  await store.dispatch('assignAgent', {
    conversationId: currentChat.value?.id,
    agentId: activeAgentBot.value.id,
    assigneeType: 'AgentBot',
  });
  useAlert('已切换为智能客服');
};

const fetchInboxAgentBot = async inboxId => {
  if (!inboxId) return;

  await Promise.all([
    store.dispatch('agentBots/get'),
    store.dispatch('agentBots/fetchAgentBotInbox', inboxId),
  ]);
};

onMounted(() => fetchInboxAgentBot(currentChat.value?.inbox_id));
watch(() => currentChat.value?.inbox_id, fetchInboxAgentBot);
</script>

<template>
  <Banner
    v-if="showSelfAssignBanner && !showBotHandoffBanner"
    action-button-variant="ghost"
    color-scheme="secondary"
    class="mx-2 mb-2 rounded-lg !py-2"
    :banner-message="$t('CONVERSATION.NOT_ASSIGNED_TO_YOU')"
    has-action-button
    :action-button-label="$t('CONVERSATION.ASSIGN_TO_ME')"
    @primary-action="onClickSelfAssign"
  />
  <Banner
    v-if="showBotHandoffBanner"
    action-button-variant="ghost"
    color-scheme="warning"
    class="mx-2 mb-2 rounded-lg !py-2"
    banner-message="智能客服正在接待。人工接管后将由您处理，会话状态保持不变。"
    has-action-button
    action-button-label="人工客服接管"
    @primary-action="onClickBotHandoff"
  />
  <Banner
    v-if="canSwitchToAgentBot"
    action-button-variant="ghost"
    color-scheme="secondary"
    class="mx-2 mb-2 rounded-lg !py-2"
    banner-message="当前由人工客服处理。"
    has-action-button
    action-button-label="智能客服接管"
    @primary-action="switchToAgentBot"
  />
</template>
